import React, { useCallback, useEffect } from 'react';
import { ensureJwtAccessTokenRefreshed } from '../api/jwtAuth';
import {
  resetPostHogUser,
  enablePostHogCapturing,
} from '../initializers/posthog';
import { checkHasValidRefreshToken } from '../api/jwtAuth/jwtAuthApi';

type Auth = {
  isLoggedIn: boolean | undefined;
  isServerError: boolean | undefined;
  serverErrorMessage?: string;
  isLoading: boolean;
  login: (apiToken: string) => void;
  logout: () => void;
};

const AuthenticatedContext = React.createContext<Auth>({
  isLoggedIn: undefined,
  isServerError: undefined,
  isLoading: true,
  login: () => {},
  logout: () => {},
});

const LAST_ACTIVITY_STORAGE_KEY = 'flairEmployeeHubLastActivity';

export const setLastActivityTime = (): void => {
  localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
};

export const getLastActivityTime = (): number | null => {
  const stored = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
  if (stored) {
    const value = Number(stored);
    return Number.isNaN(value) ? null : value;
  }
  return null;
};

const clearLastActivityTime = (): void => {
  localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
};

export const ensureApiJwtTokenRefreshed = async (): Promise<string | null> => {
  const apiToken = getApiToken();
  if (!isApiTokenJwt(apiToken)) {
    return null;
  }
  const jwtToken = apiToken?.split(' ')[1];
  if (!jwtToken) {
    return null;
  }
  const refreshResult = await ensureJwtAccessTokenRefreshed(jwtToken);
  if (refreshResult.isRefreshed) {
    saveApiToken(`Bearer ${refreshResult.jwtAccessToken}`);
  }
  return `Bearer ${refreshResult.jwtAccessToken}`;
};

let apiTokenInMemory: string | null = null;

export const getApiToken = (): string | null => {
  return apiTokenInMemory;
};

export const isApiTokenJwt = (apiToken: string | null): boolean => {
  return apiToken !== null && apiToken.startsWith('Bearer ');
};

export const saveApiToken = (apiToken: string | null) => {
  if (apiToken && !isApiTokenJwt(apiToken)) {
    throw new Error('Only JWT tokens are supported');
  }
  apiTokenInMemory = apiToken;
};

type AuthLoggedInState = {
  isLoggedIn: boolean | undefined;
  isServerError: boolean | undefined;
  isLoading: boolean;
  serverErrorMessage?: string;
};

export const AuthProvider: React.FC<React.PropsWithChildren<unknown>> = (
  props,
) => {
  const [authState, setAuthState] = React.useState<AuthLoggedInState>({
    isLoggedIn: undefined,
    isLoading: true,
    isServerError: undefined,
  });

  useEffect(() => {
    checkHasValidRefreshToken()
      .then((checkResponse) => {
        if (checkResponse.status === 'noToken') {
          setAuthState({
            isLoggedIn: false,
            isLoading: false,
            isServerError: false,
          });
        } else if (checkResponse.status === 'hasToken') {
          setAuthState({
            isLoggedIn: true,
            isLoading: false,
            isServerError: false,
          });
          saveApiToken('Bearer ' + checkResponse.accessToken);
        } else if (checkResponse.status === 'error') {
          setAuthState({
            isLoggedIn: false,
            isLoading: false,
            isServerError: true,
            serverErrorMessage: checkResponse.errorMessage,
          });
        }
      })
      .catch((error) => {
        setAuthState({
          isLoggedIn: false,
          isLoading: false,
          isServerError: true,
          serverErrorMessage: 'Internal error: ' + error,
        });
      });
  }, [setAuthState]);

  useEffect(() => {
    if (authState.isLoggedIn === true) {
      enablePostHogCapturing();
    } else if (authState.isLoggedIn === false) {
      resetPostHogUser();
    }
  }, [authState.isLoggedIn]);

  const logout = () => {
    saveApiToken(null);
    clearLastActivityTime();
    setAuthState({ isLoggedIn: false, isLoading: false, isServerError: false });
  };

  const login = useCallback(
    (apiToken: string) => {
      saveApiToken(apiToken);
      setLastActivityTime();
      setAuthState({
        isLoggedIn: true,
        isLoading: false,
        isServerError: false,
      });
    },
    [setAuthState],
  );

  return (
    <AuthenticatedContext.Provider
      value={{
        isLoggedIn: authState.isLoggedIn,
        isLoading: authState.isLoading,
        isServerError: authState.isServerError,
        serverErrorMessage: authState.serverErrorMessage,
        login,
        logout,
      }}
      {...props}
    />
  );
};

export const useAuth = (): Auth => React.useContext(AuthenticatedContext);
